/**
 * Agent Card Component
 * Displays individual agent information in a card format
 */

import { Bot, Check, X, ExternalLink, DollarSign } from 'lucide-react';
import type { Agent } from '@/types/agent';

interface AgentCardProps {
  agent: Agent;
}

export function AgentCard({ agent }: AgentCardProps) {
  const chainNames: Record<string, string> = {
    '11155111': 'Ethereum Sepolia',
    '84532': 'Base Sepolia',
    '80002': 'Polygon Amoy',
    '1': 'Ethereum Mainnet',
    '8453': 'Base',
    '137': 'Polygon'
  };

  // Handle undefined/null chain_id gracefully
  const chainId = agent.chain_id || agent.agent_id?.split(':')[0] || 'unknown';
  const chainName = chainNames[chainId] || (chainId !== 'unknown' ? `Chain ${chainId}` : 'Unknown Chain');

  // Handle undefined active status (default to showing as inactive)
  const isActive = agent.active === true;

  // Parse updated_at date safely
  const getFormattedDate = () => {
    if (!agent.updated_at) return 'Never';
    try {
      const date = new Date(agent.updated_at);
      return isNaN(date.getTime()) ? 'Unknown' : date.toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0">
          {agent.image ? (
            <img
              src={agent.image}
              alt={agent.name || 'Agent'}
              className="w-12 h-12 rounded-lg object-cover"
              onError={(e) => {
                e.currentTarget.src = '';
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
              <Bot className="w-6 h-6 text-purple-600" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 truncate">{agent.name || 'Unknown Agent'}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">{chainName}</span>
            {agent.x402_support && (
              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                X-402
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isActive ? (
            <div className="flex items-center gap-1 text-green-600">
              <Check className="w-4 h-4" />
              <span className="text-xs font-medium">Active</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-gray-400">
              <X className="w-4 h-4" />
              <span className="text-xs font-medium">Inactive</span>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
        {agent.description || 'No description available'}
      </p>

      {/* Skills/Capabilities */}
      {agent.skills && agent.skills.length > 0 && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-1">
            {agent.skills.slice(0, 3).map((skill, idx) => (
              <span key={idx} className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded">
                {skill}
              </span>
            ))}
            {agent.skills.length > 3 && (
              <span className="text-xs px-2 py-1 bg-gray-50 text-gray-600 rounded">
                +{agent.skills.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {agent.ens && (
            <span className="flex items-center gap-1">
              <span className="font-medium">ENS:</span>
              <span className="font-mono">{agent.ens}</span>
            </span>
          )}
          {agent.price_usdc !== undefined && (
            <span className="flex items-center gap-1 text-green-600 font-medium">
              ${agent.price_usdc.toFixed(2)} USDC
            </span>
          )}
        </div>
        {agent.endpoint && (
          <a
            href={agent.endpoint}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
          >
            View
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Additional Info */}
      <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500 space-y-1">
        {agent.mcp_endpoint && (
          <div className="flex items-center gap-1">
            <span className="font-medium">MCP:</span>
            <span className="font-mono truncate">{agent.mcp_version || 'Available'}</span>
          </div>
        )}
        {agent.a2a_endpoint && (
          <div className="flex items-center gap-1">
            <span className="font-medium">A2A:</span>
            <span className="font-mono truncate">{agent.a2a_version || 'Available'}</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <span className="font-medium">Updated:</span>
          <span>{getFormattedDate()}</span>
        </div>
      </div>
    </div>
  );
}
